import { ComponentRef, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertService,
  AlertType,
  CupcakeFlavors,
  ModalComponent,
  ModalService,
  ModalSize,
  ModalType
} from '@ipreo/ngx-sprinkles';
import moment from 'moment';
import { createModal } from '../../utils/modal';
import { BreakdownFormConfirmModalComponent } from '../../components/breakdown-form-confirm-modal/breakdown-form-confirm-modal.component';
import { BreakdownSubmissionErrorModalComponent } from '../../components/breakdown-submission-error-modal/breakdown-submission-error-modal.component';
import { SubmissionConflictErrorModalComponent } from '../../components/submission-conflict-error-modal/submission-conflict-error-modal.component';
import { PermissionsFacade } from '../../facades/permissions.facade';
import { isBMEnabled } from '../../helpers/designations.helper';
import { DesignationsGridColumnName } from '../../grid/types/column.types';
import { ColumnSortModel } from '../../grid/types/sorting.types';
import {
  DESIGNATIONS_TAB_CARDS,
  getIssuesExportData,
  sortIssuesByType
} from '../../helpers/designations.helper';
import { DesignationService } from '../../services/designations.service';
import {
  DesignationBreakdown,
  DesignationDealInfo,
  DesignationEntryStatus,
  DesignationIssue,
  DesignationsCardTypes
} from '../../types/designation.types';
import {
  CONFLICT_ERROR_RESPONSE_CODE,
  LEAVE_CONFIRM_MODAL_CONFIG,
  UNPROCESSED_ENTITY_CODE
} from '../../utils/breakdowns.const';
import { ExcelTable } from '../../utils/export/excel/table';
import { generateExcelWorkBook } from '../../utils/export/excel/workbook';
import { ExcelWorkSheet } from '../../utils/export/excel/worksheet';
import { DealActionTypes } from '../deal/actions';
import {
  getDeal,
  selectActiveTab,
  getDesignationsCardSortModelSelector,
  selectIsBreakdownFormDirty,
  selectIssuesByDesignationStatus
} from '../reducer';
import {
  CloseBreakdownErrorModal,
  CloseBreakdownFormConfirmModal,
  CloseSubmissionConflictErrorModal,
  CompleteDesignationBreakdownSubmission,
  DesignationActionTypes,
  ExportDesignationIssues,
  LoadAllotments,
  LoadAllotmentsFailure,
  LoadAllotmentsSuccess,
  LoadBreakdown,
  LoadBreakdownFailure,
  LoadBreakdownSuccess,
  LoadDesiEntryStatus,
  LoadDesiEntryStatusFailure,
  LoadDesiEntryStatusSuccess,
  LoadDesignationDealInfo,
  LoadDesignationDealInfoFailure,
  LoadDesignationDealInfoSuccess,
  LoadDesignationIssuesFailure,
  LoadDesignationIssuesSuccess,
  OpenBreakdownErrorModal,
  OpenSubmissionConflictErrorModal,
  ReloadDesignationsTab,
  SetDesignationBreakdownFailureError,
  SubmitDesignationBreakdown,
  SubmitDesignationBreakdownFailure,
  SubmitDesignationBreakdownSuccess,
  UpdateDesignationStatus,
  UpdateSelectedAllotments
} from './actions';
import { DesignationEntryState } from './state';

@Injectable()
export class DesignationsEffects {
  private breakdownSubmissionErrorModalComponent: ComponentRef<ModalComponent>;
  private submissionConflictErrorModalComponent: ComponentRef<ModalComponent>;
  private breakdownFormConfirmModalComponent: ComponentRef<ModalComponent>;

  public loadAllotments$: Observable<
    LoadAllotmentsSuccess | LoadAllotmentsFailure
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.LoadAllotments),
      withLatestFrom(this.store.select(getDeal)),
      switchMap(([, deal]) =>
        this.designationService.getAllotmentsById({ deal }).pipe(
          map((allotments: any) => new LoadAllotmentsSuccess(allotments)),
          catchError((error) =>
            of(new LoadAllotmentsFailure(error && (error as Error).message))
          )
        )
      )
    )
  );

  public loadDesignationIssues$: Observable<
    LoadDesignationIssuesSuccess | LoadDesignationIssuesFailure
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.LoadDesignationIssues),
      withLatestFrom(this.store.select(getDeal)),
      switchMap(([, deal]) =>
        this.designationService.getDesignationIssues().pipe(
          map((issues: any) => new LoadDesignationIssuesSuccess(issues)),
          catchError((error) =>
            of(
              new LoadDesignationIssuesFailure(
                error && (error as Error).message
              )
            )
          )
        )
      )
    )
  );

  public loadDesignationDealInfo$: Observable<
    LoadDesignationDealInfoSuccess | LoadDesignationDealInfoFailure
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.LoadDesignationDealInfo),
      withLatestFrom(this.store.select(getDeal)),
      switchMap(([, deal]) =>
        this.designationService.getDesignationDealInfoById({ deal }).pipe(
          map(
            (designationDealInfo: DesignationDealInfo) =>
              new LoadDesignationDealInfoSuccess(designationDealInfo)
          ),
          catchError((error) =>
            of(
              new LoadDesignationDealInfoFailure(
                error && (error as Error).message
              )
            )
          )
        )
      )
    )
  );

  public checkForPermission$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DesignationActionTypes.LoadDesiEntryStatusSuccess,
          DesignationActionTypes.LoadDesiEntryStatusFailure
        ),
        withLatestFrom(this.permissionsFacade.permissions$),
        withLatestFrom(this.store.select(selectActiveTab)),
        tap(([[action, permissions], activeTab]) => {
          if (activeTab !== 'Designations') {
            return;
          }

          const desiEntryData: DesignationEntryState = (action as any).data;
          const canActivate =
            permissions.isDesignationsEnabled &&
            desiEntryData &&
            isBMEnabled(desiEntryData);

          if (!canActivate) {
            window.location.href = window.location.href.replace(
              '/' + 'designations',
              ''
            );
          }
        })
      ),
    { dispatch: false }
  );

  public onDealDetailsSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DealActionTypes.LoadSuccess,
        DesignationActionTypes.ReloadDesignationsTab,
        DesignationActionTypes.LoadDesignationsTab
      ),
      withLatestFrom(this.permissionsFacade.permissions$),
      mergeMap(([action, permissions]) => {
        if (!permissions.isDesignationsEnabled) {
          return [];
        }

        const isLoadSuccess = action['type'] !== DealActionTypes.LoadSuccess;
        return [new LoadDesiEntryStatus(isLoadSuccess)];
      })
    )
  );

  public loadDesignationsTab$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.LoadDesignationsTab),
      mergeMap(() => [
        new LoadAllotments(),
        new LoadDesignationDealInfo(),
        new LoadBreakdown()
      ])
    )
  );

  public triggerReloadDesignationsTab$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.TriggerReloadDesignationsTab),
      withLatestFrom(this.store.select(selectIsBreakdownFormDirty)),
      switchMap(([, isFormDirty]) => {
        if (isFormDirty) {
          this.breakdownFormConfirmModalComponent = createModal(
            this.modalService,
            {
              type: ModalType.View,
              view: BreakdownFormConfirmModalComponent,
              closeButton: false,
              closeOnEscape: false,
              closeOnOverlayClick: false,
              size: ModalSize.Responsive,
              inputs: { width: 'min(480px,80vw)' },
              modalContentClass: 'desiBreakdownFormModal',
              showOverlay: true,
              context: {
                ...LEAVE_CONFIRM_MODAL_CONFIG,
                response: new Subject<{ closeAllowed: boolean }>()
              }
            }
          );

          return this.breakdownFormConfirmModalComponent.instance.response.pipe(
            switchMap(({ closeAllowed }) => {
              if (closeAllowed) {
                return of(new ReloadDesignationsTab());
              } else {
                return EMPTY;
              }
            })
          );
        } else {
          return of(new ReloadDesignationsTab());
        }
      })
    )
  );

  public reloadDesignationsTab$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.ReloadDesignationsTab),
      mergeMap(() => [
        new UpdateSelectedAllotments(undefined),
        new LoadAllotments(),
        new LoadDesignationDealInfo(),
        new LoadBreakdown()
      ])
    )
  );

  public submitDesignationBreakdown$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<SubmitDesignationBreakdown>(
        DesignationActionTypes.SubmitDesignationBreakdown
      ),
      withLatestFrom(this.store.select(getDeal)),
      switchMap(([action, deal]) =>
        this.designationService
          .submitDesignationBreakdown(action.payload, deal.id)
          .pipe(
            map(
              (response: DesignationBreakdown[]) =>
                new SubmitDesignationBreakdownSuccess(response)
            ),
            catchError((error: Error | HttpErrorResponse) => {
              const statusCode = (error as HttpErrorResponse).status;

              if (statusCode === UNPROCESSED_ENTITY_CODE) {
                return this.getUnProcessedEntityAction(error);
              }

              if (statusCode === CONFLICT_ERROR_RESPONSE_CODE) {
                return of(new OpenSubmissionConflictErrorModal());
              }

              return of(
                new SubmitDesignationBreakdownFailure((error as Error).message)
              );
            }),
            finalize(() => {
              this.store.dispatch(new CompleteDesignationBreakdownSubmission());
            })
          )
      )
    )
  );

  public submitDesignationBreakdownSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SubmitDesignationBreakdownSuccess>(
          DesignationActionTypes.SubmitDesignationBreakdownSuccess
        ),
        tap((action: SubmitDesignationBreakdownSuccess) => {
          const response: DesignationBreakdown[] = action.payload;
          if (response?.length > 0) {
            this.alertService.create({
              body: 'Designations have been successfully validated and saved for the selected allotments.',
              alertType: AlertType.toast,
              color: CupcakeFlavors.Success,
              icon: 'fa fa-check-circle'
            });

            this.store.dispatch(new LoadBreakdown());
          }
        })
      ),
    { dispatch: false }
  );

  public loadBreakdown$: Observable<
    LoadBreakdownSuccess | LoadBreakdownFailure
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.LoadBreakdown),
      withLatestFrom(this.store.select(getDeal)),
      switchMap(([, deal]) =>
        this.designationService.getDesiBreakdownsByGii(deal).pipe(
          map((data: DesignationBreakdown[]) => new LoadBreakdownSuccess(data)),
          catchError((error: Error | HttpErrorResponse) =>
            of(new LoadBreakdownFailure(error && (error as Error).message))
          )
        )
      )
    )
  );

  public loadDesiEntryStatus$: Observable<
    LoadDesiEntryStatusSuccess | LoadDesiEntryStatusFailure
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(DesignationActionTypes.LoadDesiEntryStatus),
      withLatestFrom(this.store.select(getDeal)),
      switchMap(([, deal]) =>
        this.designationService.getDesiEntryStatusByGii(deal).pipe(
          map(
            (data: DesignationEntryStatus) =>
              new LoadDesiEntryStatusSuccess(data)
          ),
          catchError((error: Error | HttpErrorResponse) =>
            of(
              new LoadDesiEntryStatusFailure(error && (error as Error).message)
            )
          )
        )
      )
    )
  );

  public updateDesignationStatus$: Observable<UpdateDesignationStatus> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(
          DesignationActionTypes.LoadAllotmentsSuccess,
          DesignationActionTypes.LoadBreakdownSuccess
        ),
        switchMap((_action: LoadAllotmentsSuccess | LoadBreakdownSuccess) =>
          of(new UpdateDesignationStatus())
        )
      )
    );

  public errorToast$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DesignationActionTypes.SubmitDesignationBreakdownFailure,
          DesignationActionTypes.LoadAllotmentsFailure,
          DesignationActionTypes.LoadDesignationDealInfoFailure,
          DesignationActionTypes.LoadBreakdownFailure,
          DesignationActionTypes.LoadDesignationIssuesFailure,
          DesignationActionTypes.LoadDesiEntryStatusFailure
        ),
        tap(
          (
            action:
              | SubmitDesignationBreakdownFailure
              | LoadAllotmentsFailure
              | LoadDesignationDealInfoFailure
              | LoadBreakdownFailure
              | LoadDesignationIssuesFailure
              | LoadDesiEntryStatusFailure
          ) => {
            const response: string = action.payload;
            if (response) {
              this.alertService.create({
                body: response,
                alertType: AlertType.toast,
                color: CupcakeFlavors.Danger,
                icon: 'fa fa-exclamation-triangle',
                timeout: 8000
              });
            }
          }
        )
      ),
    { dispatch: false }
  );

  public openBreakdownErrorModal$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OpenBreakdownErrorModal>(
          DesignationActionTypes.OpenBreakdownErrorModal
        ),
        tap(() => {
          this.breakdownSubmissionErrorModalComponent = createModal(
            this.modalService,
            {
              type: ModalType.View,
              view: BreakdownSubmissionErrorModalComponent,
              closeOnEscape: false,
              closeButton: false,
              closeOnOverlayClick: false,
              size: ModalSize.Large,
              modalContentClass: 'submissions-modal-drag',
              inputs: { width: 'min(550px, 80vw)' }
            }
          );
        })
      ),
    { dispatch: false }
  );

  public closeBreakdownErrorModal$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CloseBreakdownErrorModal>(
          DesignationActionTypes.CloseBreakdownErrorModal
        ),
        tap(() => {
          if (this.breakdownSubmissionErrorModalComponent) {
            this.modalService.destroy(
              this.breakdownSubmissionErrorModalComponent
            );
          }
        })
      ),
    { dispatch: false }
  );

  public openSubmissionConflictErrorModal$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OpenSubmissionConflictErrorModal>(
          DesignationActionTypes.OpenSubmissionConflictErrorModal
        ),
        tap(() => {
          this.submissionConflictErrorModalComponent = createModal(
            this.modalService,
            {
              type: ModalType.View,
              view: SubmissionConflictErrorModalComponent,
              closeOnEscape: false,
              closeButton: false,
              closeOnOverlayClick: false,
              size: ModalSize.Large,
              modalContentClass: 'submission-conlict-modal-drag',
              inputs: { width: 'min(550px, 80vw)' }
            }
          );
        })
      ),
    { dispatch: false }
  );

  public closeSubmissionConflictErrorModal$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CloseSubmissionConflictErrorModal>(
          DesignationActionTypes.CloseSubmissionConflictErrorModal
        ),
        tap(() => {
          if (this.submissionConflictErrorModalComponent) {
            this.modalService.destroy(
              this.submissionConflictErrorModalComponent
            );
          }
        })
      ),
    { dispatch: false }
  );

  public closeBreakdownFormConfirmModal$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CloseBreakdownFormConfirmModal>(
          DesignationActionTypes.CloseBreakdownFormConfirmModal
        ),
        tap(() => {
          if (this.breakdownFormConfirmModalComponent) {
            this.modalService.destroy(this.breakdownFormConfirmModalComponent);
          }
        })
      ),
    { dispatch: false }
  );

  public exportDesignationIssues$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ExportDesignationIssues>(
          DesignationActionTypes.ExportDesignationIssues
        ),
        withLatestFrom(
          this.store.select(
            selectIssuesByDesignationStatus(
              DesignationsCardTypes.DesignationsActive
            )
          ),
          this.store.select(
            selectIssuesByDesignationStatus(
              DesignationsCardTypes.DesignationsFinal
            )
          ),
          this.store.select(
            getDesignationsCardSortModelSelector(
              DesignationsCardTypes.DesignationsActive
            )
          ),
          this.store.select(
            getDesignationsCardSortModelSelector(
              DesignationsCardTypes.DesignationsFinal
            )
          )
        ),
        switchMap(
          ([, activeIssues, finalIssues, activeSortModel, finalSortModel]: [
            ExportDesignationIssues,
            DesignationIssue[],
            DesignationIssue[],
            ColumnSortModel<DesignationsGridColumnName>[],
            ColumnSortModel<DesignationsGridColumnName>[]
          ]) => {
            const sortedActiveIssues = sortIssuesByType(
              activeIssues,
              activeSortModel,
              DesignationsCardTypes.DesignationsActive
            );
            const sortedFinalIssues = sortIssuesByType(
              finalIssues,
              finalSortModel,
              DesignationsCardTypes.DesignationsFinal
            );
            const currentTime = moment().format('MM_DD_YYYY_hh_mm_ss A');
            const fileName = `Designated deals_${currentTime}_Confidential`;
            const activeIssuesExportData = getIssuesExportData(
              sortedActiveIssues,
              DesignationsCardTypes.DesignationsActive,
              this.locale
            );
            const finalIssuesExportData = getIssuesExportData(
              sortedFinalIssues,
              DesignationsCardTypes.DesignationsFinal,
              this.locale
            );
            const activeIssuesTable = new ExcelTable(activeIssuesExportData, {
              shouldBeFullyVisible: true
            });
            const finalIssuesTable = new ExcelTable(finalIssuesExportData, {
              shouldBeFullyVisible: true
            });
            const activeWorksheet = new ExcelWorkSheet(
              DESIGNATIONS_TAB_CARDS[0].header,
              activeIssuesTable
            );
            const finalWorksheet = new ExcelWorkSheet(
              DESIGNATIONS_TAB_CARDS[1].header,
              finalIssuesTable
            );
            generateExcelWorkBook(fileName, [activeWorksheet, finalWorksheet]);

            return EMPTY;
          }
        )
      ),
    { dispatch: false }
  );

  constructor(
    public designationService: DesignationService,
    private actions$: Actions,
    private store: Store,
    private alertService: AlertService,
    private modalService: ModalService,
    private readonly permissionsFacade: PermissionsFacade,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  private getUnProcessedEntityAction(error: Error | HttpErrorResponse) {
    if (
      (error as HttpErrorResponse)?.error?.errorCode ===
      'PriorityRulesViolation'
    ) {
      return of(
        new SetDesignationBreakdownFailureError(
          (error as HttpErrorResponse).error
        )
      );
    } else {
      const errorMessage =
        (error as HttpErrorResponse).error?.errorDescription ||
        (error as Error).message;
      return of(new SubmitDesignationBreakdownFailure(errorMessage));
    }
  }
}
